import { Static, Type } from '@sinclair/typebox'

export const AdminHealthServiceStatus = Type.Union([
    Type.Literal('ok'),
    Type.Literal('failed'),
])

export type AdminHealthServiceStatus = Static<typeof AdminHealthServiceStatus>

export const AdminHealthService = Type.Object({
    key: Type.String(),
    label: Type.String(),
    status: AdminHealthServiceStatus,
    // A dependency that answers slowly looks identical to a healthy one without
    // this: "up but dying" is otherwise invisible.
    durationMs: Type.Number(),
})

export type AdminHealthService = Static<typeof AdminHealthService>

export const AdminHealthQueue = Type.Object({
    queueName: Type.String(),
    jobsWaiting: Type.Number(),
    jobsActive: Type.Number(),
    oldestJobAgeSeconds: Type.Number(),
    // A slot that never frees shows here long before the waiting age moves:
    // the queue keeps draining on the remaining slots until the last one goes.
    oldestActiveJobAgeSeconds: Type.Number(),
})

export type AdminHealthQueue = Static<typeof AdminHealthQueue>

export const AdminOrphanRunsTenant = Type.Object({
    tenantId: Type.String(),
    tenantName: Type.String(),
    total: Type.Number(),
})

export type AdminOrphanRunsTenant = Static<typeof AdminOrphanRunsTenant>

// Runs left RUNNING or RATE_LIMITED by a worker exit path that never posted a
// final status. Nothing in Redis reveals them: only flow_run does.
export const AdminHealthOrphanRuns = Type.Object({
    idleHours: Type.Number(),
    total: Type.Number(),
    byStatus: Type.Record(Type.String(), Type.Number()),
    topTenants: Type.Array(AdminOrphanRunsTenant),
})

export type AdminHealthOrphanRuns = Static<typeof AdminHealthOrphanRuns>

// Tells whether the indicators the accounts panel relies on are still being
// refreshed — stale numbers rendered confidently are worse than none.
export const AdminHealthIndicatorsFreshness = Type.Object({
    lastComputedAt: Type.Union([Type.String(), Type.Null()]),
    periodsAvailable: Type.Number(),
    tenantsCovered: Type.Number(),
    pushFailures: Type.Number(),
})

export type AdminHealthIndicatorsFreshness = Static<
    typeof AdminHealthIndicatorsFreshness
>

// Every successful system-job run is deleted from Redis on completion, so
// "last succeeded" cannot exist. The next scheduled fire is the only signal:
// a `next` stuck in the past proves the scheduler stopped turning.
export const AdminSystemJob = Type.Object({
    name: Type.String(),
    cron: Type.String(),
    nextRunAt: Type.Union([Type.String(), Type.Null()]),
    isOverdue: Type.Boolean(),
    lastFailureAt: Type.Union([Type.String(), Type.Null()]),
    lastFailureReason: Type.Union([Type.String(), Type.Null()]),
    // How many times this job gave up inside the retained window — a blip and a
    // chronic failure otherwise look identical.
    failureCount: Type.Number(),
})

export type AdminSystemJob = Static<typeof AdminSystemJob>

export const AdminWorkerMachine = Type.Object({
    id: Type.String(),
    ip: Type.String(),
    cpuUsagePercentage: Type.Number(),
    ramUsagePercentage: Type.Number(),
    diskUsagePercentage: Type.Number(),
    lastSeenAt: Type.String(),
})

export type AdminWorkerMachine = Static<typeof AdminWorkerMachine>

// The values that actually govern runtime behaviour. Reading them here answers
// "why was my flow killed after a minute" without opening a shell.
export const AdminRuntimeConfig = Type.Object({
    environment: Type.String(),
    edition: Type.String(),
    // Where piece code actually runs — the single most load-bearing fact when
    // a behaviour differs between local and production.
    executionMode: Type.String(),
    executeWithLambda: Type.Boolean(),
    // The throughput ceiling: reads directly against the queue backlog above.
    flowWorkerConcurrency: Type.Number(),
    scheduledWorkerConcurrency: Type.Number(),
    fairQueueEnabled: Type.Boolean(),
    // Past this, a flow's runs go RATE_LIMITED. Code constants, not env vars:
    // changing them takes a release.
    flowRateLimitMaxExecutions: Type.Number(),
    flowRateLimitWindowSeconds: Type.Number(),
    flowTimeoutSeconds: Type.Number(),
    triggerTimeoutSeconds: Type.Number(),
    webhookTimeoutSeconds: Type.Number(),
    pausedFlowTimeoutDays: Type.Number(),
    executionRetentionDays: Type.Number(),
    // Caps an upload and a run's output alike: a run above it fails with
    // "Execution Output is too large", well before Lambda's 6 MB.
    maxFileSizeMb: Type.Number(),
    // Only jobs that fail on the platform side are retried; a flow ending in
    // FAILED completes its job. The delay doubles at each retry.
    jobMaxAttempts: Type.Number(),
    jobRetryBaseDelaySeconds: Type.Number(),
    // Consecutive trigger failures after which the flow disables itself. Polling
    // triggers only. The upstream alert email goes to EE alert recipients over
    // SMTP, neither of which Khub sets up: the disable is silent.
    triggerFailuresThreshold: Type.Number(),
})

export type AdminRuntimeConfig = Static<typeof AdminRuntimeConfig>

export const AdminRedisMemory = Type.Object({
    usedBytes: Type.Number(),
    peakBytes: Type.Union([Type.Number(), Type.Null()]),
    maxBytes: Type.Union([Type.Number(), Type.Null()]),
})

export type AdminRedisMemory = Static<typeof AdminRedisMemory>

export const AdminHealthOverview = Type.Object({
    services: Type.Array(AdminHealthService),
    // Unix seconds. The health check caches per process, so two API containers
    // can answer with different timestamps — the UI shows this one.
    servicesCheckedAt: Type.Number(),
    queues: Type.Array(AdminHealthQueue),
    workersOnline: Type.Number(),
    indicatorsFreshness: AdminHealthIndicatorsFreshness,
    systemJobs: Type.Array(AdminSystemJob),
    // Null while the first cross-tenant count is pending or failed.
    orphanRuns: Type.Union([AdminHealthOrphanRuns, Type.Null()]),
    // Last run of the Corp business-plan sync, from its own state table.
    businessPlanSyncedAt: Type.Union([Type.String(), Type.Null()]),
    version: Type.String(),
    machines: Type.Array(AdminWorkerMachine),
    runtimeConfig: AdminRuntimeConfig,
    // Null when Khub does not run on Redis.
    redisMemory: Type.Union([AdminRedisMemory, Type.Null()]),
})

export type AdminHealthOverview = Static<typeof AdminHealthOverview>
